<template>
  <div>
    <basic-container>
      <avue-crud :option="tableOption" :data="tableData" :table-loading="tableLoading" :page="page" ref="crud"
        @search-change="searchChange" @refresh-change="refreshChange(page)" @selection-change="handleSelectionChange"
        @size-change="pageSizeChange" @current-change="pageChange">
        <template slot="menuLeft">
          <el-button type="primary" icon="el-icon-plus" size="small" @click.stop="handleAdd">新增分组</el-button>
          <el-button type="danger" icon="el-icon-delete" size="small" plain @click.stop="handleDelete">批量删除</el-button>
        </template>
        <!-- 类型徽标列 -->
        <template slot="type" slot-scope="scope">
          <el-tag :color="getTypeColor(scope.row.type) + '20'"
            :style="{ color: getTypeColor(scope.row.type), borderColor: getTypeColor(scope.row.type) }" size="small">
            {{ getTypeLabel(scope.row.type) }}
          </el-tag>
        </template>
        <!-- 价格配置概览列 -->
        <template slot="priceConfigSummary" slot-scope="scope">
          <el-tag v-if="getEnabledPriceCount(scope.row) > 0" type="success" size="small" effect="plain">
            <i class="el-icon-money"></i> 已配置 {{ getEnabledPriceCount(scope.row) }} 项
          </el-tag>
          <el-tag v-else type="info" size="small" effect="plain">未配置</el-tag>
        </template>
        <template slot-scope="scope" slot="menu">
          <el-button type="text" icon="el-icon-edit" size="small" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button type="text" icon="el-icon-view" size="small" @click="handleView(scope.row)">查看明细</el-button>
        </template>
      </avue-crud>

      <!-- 新增 / 编辑分组弹窗 -->
      <el-dialog :title="isEdit ? '编辑分组' : '新增分组'" :visible.sync="dialogVisible" width="780px"
        custom-class="ag-dialog" :close-on-click-modal="false" @close="resetForm">
        <el-form :model="formData" :rules="formRules" ref="form" label-width="110px" size="small">
          <!-- 基础信息 -->
          <div class="ag-section ag-section--base">
            <div class="ag-section__head">
              <i class="el-icon-info-circle"></i>
              <span class="ag-section__title">基础信息</span>
            </div>
            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="分组名称" prop="groupName">
                  <el-input v-model="formData.groupName" placeholder="请输入分组名称" maxlength="30" show-word-limit />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="分组类型" prop="type">
                  <el-select v-model="formData.type" placeholder="请选择分组类型" style="width: 100%"
                    @change="handleTypeChange">
                    <el-option v-for="item in typeOptions" :key="item.value" :label="item.label"
                      :value="item.value">
                      <span :style="{ color: item.color }">● </span>{{ item.label }}
                    </el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="排序" prop="sort">
                  <el-input-number v-model="formData.sort" :min="0" :max="9999" :precision="0"
                    controls-position="right" placeholder="数字越小越靠前" style="width: 100%" />
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 价格配置 -->
          <template v-if="formData.type">
            <div v-if="currentPriceSections.length === 0" class="ag-empty">
              <i class="el-icon-info"></i>
              <span>当前类型暂无可配置的价格字段</span>
            </div>
            <div v-for="section in currentPriceSections" :key="section.key" class="ag-section">
              <div class="ag-section__head">
                <i :class="section.icon || 'el-icon-collection-tag'"></i>
                <span class="ag-section__title">{{ section.title }}</span>
                <span class="ag-section__desc">{{ section.desc }}</span>
                <el-button v-if="getUnusedFields(section).length > 0" type="text" size="mini"
                  icon="el-icon-circle-plus-outline"
                  @click="enableAllInSection(section)">全部启用</el-button>
              </div>
              <!-- 已启用字段列表 -->
              <div v-if="getEnabledFields(section).length > 0" class="ag-fields">
                <el-form-item v-for="field in getEnabledFields(section)" :key="field.prop"
                  :label="field.label" :prop="field.prop">
                  <el-input v-model="formData[field.prop]" :placeholder="field.placeholder || '请输入'"
                    type="number" clearable>
                    <span slot="suffix" class="ag-suffix">{{ field.suffix }}</span>
                    <el-button slot="append" icon="el-icon-close" title="移除该字段"
                      @click="disableField(field.prop)"></el-button>
                  </el-input>
                </el-form-item>
              </div>
              <!-- 未启用字段 chip -->
              <div v-if="getUnusedFields(section).length > 0" class="ag-chips">
                <span class="ag-chips__hint"><i class="el-icon-plus"></i> 点击添加：</span>
                <el-tag v-for="field in getUnusedFields(section)" :key="field.prop" class="ag-chip"
                  type="info" effect="plain" hit @click.native.stop="enableField(field.prop)">
                  + {{ field.label }}
                </el-tag>
              </div>
            </div>
          </template>
          <div v-else class="ag-empty">
            <i class="el-icon-arrow-up"></i>
            <span>请先选择「分组类型」，再配置对应价格字段</span>
          </div>

          <!-- 增项配置（新增与编辑均可用） -->
          <div class="ag-section ag-section--extra">
            <div class="ag-section__head">
              <i class="el-icon-magic-stick"></i>
              <span class="ag-section__title">增项配置</span>
              <span class="ag-section__desc">
                {{ isEdit ? '动态表单字段费用项，配好后费用计算自动生效' : '可先添加增项，保存分组后一并创建' }}
              </span>
              <el-button type="primary" size="mini" icon="el-icon-plus" plain
                @click="openExtraDialog()">添加增项</el-button>
            </div>
            <el-table :data="extraItems" v-loading="extraLoading" size="mini" border stripe
              empty-text="暂无增项配置">
              <el-table-column label="字段名" prop="fieldName" min-width="140" show-overflow-tooltip />
              <el-table-column label="商户别名" prop="merchantAliasName" min-width="140" show-overflow-tooltip />
              <el-table-column label="计算方式" width="110" align="center">
                <template slot-scope="scope">
                  <el-tag size="mini"
                    :type="{
                      MULTIPLY: 'warning', DIRECT: 'success', FIXED: 'info', AREA_TOTAL: 'danger'
                    }[scope.row.calcType] || 'info'"
                    effect="plain">
                    {{ scope.row.calcType === 'MULTIPLY' ? '数量×单价' : scope.row.calcType === 'FIXED' ? '固定金额' : scope.row.calcType === 'AREA_TOTAL' ? '面积总计×单价' : '填多少算多少' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column :label="'金额 / 单价'" prop="unitPrice" width="110" align="right">
                <template slot-scope="scope">
                  <span v-if="needPriceInput(scope.row.calcType) && scope.row.unitPrice != null">
                    <span style="color:#e6a23c;font-weight:600">{{ scope.row.unitPrice }}</span>
                    <span style="color:#909399"> 元</span>
                  </span>
                  <span v-else style="color:#c0c4cc">-</span>
                </template>
              </el-table-column>
              <el-table-column label="阈值" width="90" align="right">
                <template slot-scope="scope">
                  <span v-if="(scope.row.calcType === 'AREA_TOTAL' || scope.row.calcType === 'MULTIPLY') && scope.row.threshold != null && scope.row.threshold !== ''">
                    {{ scope.row.threshold }}
                  </span>
                  <span v-else style="color:#c0c4cc">-</span>
                </template>
              </el-table-column>
              <el-table-column :label="extraMinAmountHeader" width="100" align="right">
                <template slot-scope="scope">
                  <span v-if="scope.row.minAmount != null && scope.row.minAmount !== ''">
                    <span style="color:#f56c6c;font-weight:600">{{ scope.row.minAmount }}</span>
                    <span style="color:#909399"> 元</span>
                  </span>
                  <span v-else style="color:#c0c4cc">-</span>
                </template>
              </el-table-column>
              <el-table-column label="互斥组" width="130" align="center">
                <template slot-scope="scope">
                  <el-tag v-if="scope.row.mutexGroup" size="mini" effect="plain"
                    :color="getMutexGroupColor(scope.row.mutexGroup) + '20'"
                    :style="{ color: getMutexGroupColor(scope.row.mutexGroup), borderColor: getMutexGroupColor(scope.row.mutexGroup) }">
                    {{ scope.row.mutexGroup }}
                  </el-tag>
                  <span v-else style="color:#c0c4cc">-</span>
                </template>
              </el-table-column>
              <el-table-column label="互斥优先级" width="100" align="center">
                <template slot-scope="scope">
                  <span v-if="scope.row.mutexGroup">{{ scope.row.mutexPriority }}</span>
                  <span v-else style="color:#c0c4cc">-</span>
                </template>
              </el-table-column>
              <el-table-column label="启用" width="80" align="center">
                <template slot-scope="scope">
                  <el-switch :value="scope.row.enabled === 1"
                    @change="toggleExtraEnabled(scope.row, $event)" />
                </template>
              </el-table-column>
              <el-table-column label="计入商家" width="80" align="center">
                <template slot-scope="scope">
                  <el-switch :value="scope.row.includeInMerchant === 1"
                    @change="toggleIncludeInMerchant(scope.row, $event)" />
                </template>
              </el-table-column>
              <el-table-column label="商家费用模式" width="170" align="center">
                <template slot-scope="scope">
                  <el-tag v-if="scope.row.merchantAreaFlag === 1" type="success" size="mini" effect="plain">
                    <i class="el-icon-money"></i> 面积×商家单价
                  </el-tag>
                  <el-tag v-else-if="scope.row.includeInMerchant === 1" type="primary" size="mini" effect="plain">
                    <i class="el-icon-share"></i> 并入师傅金额
                  </el-tag>
                  <el-tag v-else type="info" size="mini" effect="plain">不进商家</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="130" align="center">
                <template slot-scope="scope">
                  <el-button type="text" size="mini" icon="el-icon-edit"
                    @click="openExtraDialog(scope.row)">编辑</el-button>
                  <el-button type="text" size="mini" icon="el-icon-delete" style="color:#f56c6c"
                    @click="deleteExtra(scope.row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="dialogVisible = false">取 消</el-button>
          <el-button type="primary" :loading="submitLoading" @click="submitForm">确 定</el-button>
        </div>
      </el-dialog>

      <!-- 分组明细弹窗 -->
      <group-details :visible="detailVisible" :group-id="currentGroupId" @close="closeDetail" />

      <!-- 增项新增/编辑子弹窗 -->
      <el-dialog :title="extraForm.id ? '编辑增项' : '添加增项'"
        :visible.sync="extraDialogVisible" width="780px" append-to-body :close-on-click-modal="false">
        <el-form :model="extraForm" :rules="extraRules" ref="extraForm" label-width="100px" size="small">
          <el-form-item label="字段名" prop="fieldName">
            <el-input v-model="extraForm.fieldName" placeholder="须与后台动态表单字段名完全一致"
              maxlength="50" show-word-limit />
          </el-form-item>
          <!-- 字段别名 -->
          <el-form-item label="字段别名" prop="alias">
            <el-input v-model="extraForm.aliasName" placeholder="请输入字段别名，不填则显示字段名"
              maxlength="50" show-word-limit />
          </el-form-item>
          <!-- 商户别名 -->
          <el-form-item label="商户别名" prop="merchantAliasName">
            <el-input v-model="extraForm.merchantAliasName" placeholder="请输入商户别名"
              maxlength="50" show-word-limit />
          </el-form-item>
          <el-form-item label="计算方式" prop="calcType">
            <div class="calc-type-grid">
              <div v-for="opt in calcTypeOptions" :key="opt.value"
                :class="['calc-type-card', { 'is-active': extraForm.calcType === opt.value }]"
                @click="extraForm.calcType = opt.value">
                <div class="calc-type-card__icon" :style="{ background: opt.bg, color: opt.color }">
                  <i :class="opt.icon"></i>
                </div>
                <div class="calc-type-card__body">
                  <div class="calc-type-card__title">{{ opt.label }}</div>
                  <div class="calc-type-card__desc">{{ opt.desc }}</div>
                </div>
                <i v-if="extraForm.calcType === opt.value" class="el-icon-success calc-type-card__check"></i>
              </div>
            </div>
          </el-form-item>
          <el-form-item v-if="needPriceInput(extraForm.calcType)"
            :label="extraForm.calcType === 'FIXED' ? '金额' : '单价'"
            prop="unitPrice">
            <el-input-number v-model="extraForm.unitPrice" :min="0" :precision="2" :step="1"
              controls-position="right"
              :placeholder="extraForm.calcType === 'FIXED' ? '请输入固定金额，如 100' : '请输入单价，如 30'"
              style="width: 100%" />
            <div class="form-tip">
              <i class="el-icon-info"></i>
              {{
                extraForm.calcType === 'FIXED' ? '启用后该字段将按此金额计算（与填入值无关）'
                  : extraForm.calcType === 'AREA_TOTAL' ? '费用 = 面积总计 × 单价'
                    : '费用 = 填入数量 × 单价'
              }}
            </div>
          </el-form-item>
          <!-- 阈值：MULTIPLY 为「数量阈值」，AREA_TOTAL 为「面积阈值」 -->
          <el-form-item v-if="extraForm.calcType === 'MULTIPLY' || extraForm.calcType === 'AREA_TOTAL'"
            :label="extraForm.calcType === 'MULTIPLY' ? '数量阈值' : '面积阈值'" prop="threshold">
            <el-input-number v-model="extraForm.threshold" :min="0" :precision="2" :step="1"
              controls-position="right"
              :placeholder="extraForm.calcType === 'MULTIPLY' ? '选填，不填则无阈值' : '选填，面积总计不足此值时按此值计费'"
              style="width: 100%" />
            <div class="form-tip">
              <i class="el-icon-info"></i>
              <template v-if="extraForm.calcType === 'MULTIPLY'">
                填入数量 < 阈值时按保底金额计费；数量 ≥ 阈值时按「保底金额 + (数量-阈值)×单价」计算
              </template>
              <template v-else>
                面积总计 < 阈值时按阈值计费，不填则不保底
              </template>
            </div>
          </el-form-item>
          <!-- 保底金额 / 最低结算金额：MULTIPLY 为阈值保底联动，其他模式为最低结算金额 -->
          <el-form-item v-if="extraForm.calcType === 'MULTIPLY'" label="保底金额" prop="minAmount">
            <el-input-number v-model="extraForm.minAmount" :min="0" :precision="2" :step="1"
              controls-position="right" placeholder="选填，不填则无保底" style="width: 100%" />
            <div class="form-tip">
              <i class="el-icon-info"></i>
              阈值保底的「保底金额 c」，需与「数量阈值」同时配置才生效，否则按原逻辑（数量 × 单价）计算
            </div>
          </el-form-item>
          <el-form-item v-else-if="needPriceInput(extraForm.calcType)" label="价格阈值" prop="minAmount">
            <el-input-number v-model="extraForm.minAmount" :min="0" :precision="2" :step="1"
              controls-position="right"
              placeholder="选填，计算金额低于此值时按此值计费"
              style="width: 100%" />
            <div class="form-tip">
              <i class="el-icon-info"></i>
              计算金额 < 价格阈值时按阈值计费（最低保底金额），不填则不保底
            </div>
          </el-form-item>
          <el-form-item label="启用" prop="enabled">
            <el-switch :value="extraForm.enabled === 1"
              @change="extraForm.enabled = $event ? 1 : 0" />
          </el-form-item>
          <el-form-item label="计入商家">
            <el-switch :value="extraForm.includeInMerchant === 1"
              :disabled="extraForm.merchantAreaFlag === 1"
              @change="extraForm.includeInMerchant = $event ? 1 : 0" />
            <div class="form-tip" style="margin-top:0;display:inline-block;margin-left:10px">
              <i class="el-icon-info"></i>
              开启后师傅金额原样并入商家费用
            </div>
          </el-form-item>
          <!-- 商家面积计价：BUILTIN 内置字段不支持 -->
          <el-form-item v-if="extraForm.calcType !== 'BUILTIN'" label="商家面积计价">
            <el-switch :value="extraForm.merchantAreaFlag === 1"
              @change="onMerchantAreaFlagChange" />
            <div class="form-tip" style="margin-top:0;display:inline-block;margin-left:10px">
              <i class="el-icon-info"></i>
              开启后该字段商家费用 = 面积值 × 商家单价（从防水商户价格表取）；与「计入商家」互斥，开启时后者自动关闭
            </div>
          </el-form-item>
          <!-- 互斥配置 -->
          <div class="mutex-section">
            <div class="mutex-section__head">
              <i class="el-icon-connection"></i>
              <span class="mutex-section__title">互斥配置</span>
              <span class="mutex-section__desc">同分组下填相同互斥组标识的多个增项互斥择一计费</span>
            </div>
            <div class="mutex-section__tip">
              <i class="el-icon-info"></i>
              同分组下，互斥组填相同标识的多个增项是互相替代关系。例如「丙纶施工面积」和「楼下防水区域顶棚照片」填同一个互斥组，则丙纶有值时只算丙纶，丙纶没值才算照片项。
            </div>
            <el-row :gutter="16">
              <el-col :span="14">
                <el-form-item label="互斥组标识" prop="mutexGroup">
                  <el-input v-model="extraForm.mutexGroup"
                    placeholder="留空表示不参与互斥，同分组下相同标识的增项互斥择一"
                    maxlength="50" show-word-limit clearable />
                </el-form-item>
              </el-col>
              <el-col :span="10">
                <el-form-item label="互斥优先级" prop="mutexPriority">
                  <el-input-number v-model="extraForm.mutexPriority" :min="0" :max="9999" :precision="0"
                    controls-position="right" placeholder="数值越小越优先，默认0"
                    style="width: 100%" :disabled="!extraForm.mutexGroup" />
                  <div class="form-tip">
                    <i class="el-icon-info"></i>
                    数值越小越优先，同组内多个都有值时按此排序取首个
                  </div>
                </el-form-item>
              </el-col>
            </el-row>
          </div>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="extraDialogVisible = false">取 消</el-button>
          <el-button type="primary" :loading="extraSubmitLoading" @click="submitExtra">确 定</el-button>
        </div>
      </el-dialog>
    </basic-container>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  userTableOption,
  priceSectionsMap,
  allPriceProps,
  groupTypeDic,
} from "@/const/salesCloud/accountGroupOption";
import {
  findUser,
  addTask,
  editAcct,
  deleteAccountGroup,
  findExtraItem,
  addExtraItem,
  updateExtraItem,
  deleteExtraItem,
} from "@/api/sales/accountGroup";
import { getRootDepartmentId } from "@/api/work/waterProof/merchants";
import GroupDetails from "./groupDetails.vue";

const buildEmptyForm = () => {
  const base = {
    id: "",
    groupName: "",
    sort: 0,
    type: "",
    rootDepartId: "",
  };
  allPriceProps.forEach((p) => {
    base[p] = "";
  });
  return base;
};

const buildEmptyExtraForm = () => ({
  id: "",
  fieldName: "",
  aliasName: "",
  merchantAliasName: "",
  calcType: "DIRECT",
  unitPrice: 0,
  threshold: null,
  enabled: 1,
  includeInMerchant: 0,
  merchantAreaFlag: 0,
  groupId: "",
  mutexGroup: "",
  mutexPriority: 0,
  minAmount: null,
});

export default {
  name: "accountGroup",
  components: { GroupDetails },
  data() {
    return {
      tableOption: userTableOption,
      tableData: [],
      tableLoading: false,
      dialogVisible: false,
      submitLoading: false,
      isEdit: false,
      formData: buildEmptyForm(),
      // 已启用价格字段的集合（key 为 prop）
      enabledMap: {},
      typeOptions: groupTypeDic,
      formRules: {
        groupName: [{ required: true, message: "请输入分组名称", trigger: "blur" }],
        type: [{ required: true, message: "请选择分组类型", trigger: "change" }],
      },
      page: { total: 0, currentPage: 1, pageSize: 10 },
      detailVisible: false,
      currentGroupId: "",
      // 缓存搜索条件
      searchParams: {},
      // 顶级部门 ID
      rootDepartId: "",
      /* ====== 增项配置 ====== */
      extraItems: [],
      extraLoading: false,
      extraDialogVisible: false,
      extraSubmitLoading: false,
      extraForm: buildEmptyExtraForm(),
      calcTypeOptions: [
        {
          value: "DIRECT", label: "填多少算多少", desc: "用户填入的数值即为费用",
          icon: "el-icon-edit-outline", color: "#67c23a", bg: "#f0f9eb",
        },
        {
          value: "MULTIPLY", label: "数量 × 单价", desc: "按实际填写数量 × 单价计算",
          icon: "el-icon-edit-outline", color: "#e6a23c", bg: "#fdf6ec",
        },
        {
          value: "FIXED", label: "固定金额", desc: "所选即计为预设金额",
          icon: "el-icon-edit-outline", color: "#909399", bg: "#f4f4f5",
        },
        {
          value: "AREA_TOTAL", label: "面积总计 × 单价", desc: "按总面积 × 单价计算",
          icon: "el-icon-edit-outline", color: "#f56c6c", bg: "#fef0f0",
        },
      ],
      extraRules: {
        fieldName: [
          { required: true, message: "请输入字段名", trigger: "blur" },
          { max: 50, message: "字段名不能超过 50 个字符", trigger: "blur" },
        ],
        calcType: [{ required: true, message: "请选择计算方式", trigger: "change" }],
        unitPrice: [
          {
            validator: (rule, value, callback) => {
              const t = this.extraForm.calcType;
              if ((t === "MULTIPLY" || t === "AREA_TOTAL" || t === "FIXED") && (value === null || value === undefined || value === "")) {
                callback(new Error(t === "FIXED" ? "请输入固定金额" : "请输入单价"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        // MULTIPLY 模式下，数量阈值与保底金额必须同时配置（同时 > 0）或同时为空
        threshold: [
          {
            validator: (rule, value, callback) => {
              if (this.extraForm.calcType !== "MULTIPLY") return callback();
              const hasThreshold = value !== null && value !== undefined && value !== "" && Number(value) > 0;
              const hasMin = this.extraForm.minAmount !== null && this.extraForm.minAmount !== undefined && this.extraForm.minAmount !== "" && Number(this.extraForm.minAmount) > 0;
              if (hasThreshold && !hasMin) {
                return callback(new Error("数量阈值与保底金额需同时配置"));
              }
              callback();
            },
            trigger: "blur",
          },
        ],
        minAmount: [
          {
            validator: (rule, value, callback) => {
              if (this.extraForm.calcType !== "MULTIPLY") return callback();
              const hasMin = value !== null && value !== undefined && value !== "" && Number(value) > 0;
              const hasThreshold = this.extraForm.threshold !== null && this.extraForm.threshold !== undefined && this.extraForm.threshold !== "" && Number(this.extraForm.threshold) > 0;
              if (hasMin && !hasThreshold) {
                return callback(new Error("保底金额与数量阈值需同时配置"));
              }
              callback();
            },
            trigger: "blur",
          },
        ],
      },
    };
  },
  computed: {
    ...mapGetters(["permission"]),
    currentPriceSections() {
      return priceSectionsMap[this.formData.type] || [];
    },
    // 增项表格中「保底金额」列的标题：含 MULTIPLY 时强调阈值保底
    extraMinAmountHeader() {
      const hasMultiply = (this.extraItems || []).some((i) => i.calcType === "MULTIPLY");
      return hasMultiply ? "保底金额" : "价格阈值";
    },
  },
  created() {
    this.fetchRootDepartId();
    this.handleList();
  },
  methods: {
    // 计算方式是否需要金额/单价输入
    needPriceInput(t) {
      return t === "MULTIPLY" || t === "AREA_TOTAL" || t === "FIXED";
    },
    /* ====== 顶级部门 ID ====== */
    fetchRootDepartId() {
      if (this.rootDepartId) return Promise.resolve(this.rootDepartId);
      return getRootDepartmentId()
        .then((res) => {
          this.rootDepartId = res && res.data ? res.data : "";
          return this.rootDepartId;
        })
        .catch((err) => {
          console.log("fetchRootDepartId error:", err);
          return "";
        });
    },
    /* ====== 字段启用/禁用 ====== */
    isEnabled(prop) {
      return !!this.enabledMap[prop];
    },
    enableField(prop) {
      this.$set(this.enabledMap, prop, true);
    },
    disableField(prop) {
      this.$set(this.enabledMap, prop, false);
      this.$set(this.formData, prop, "");
    },
    enableAllInSection(section) {
      section.fields.forEach((f) => this.enableField(f.prop));
    },
    getEnabledFields(section) {
      return section.fields.filter((f) => this.isEnabled(f.prop));
    },
    getUnusedFields(section) {
      return section.fields.filter((f) => !this.isEnabled(f.prop));
    },
    /* ====== 类型 / 标签 / 颜色 ====== */
    getTypeLabel(type) {
      const item = groupTypeDic.find((i) => i.value === type);
      return item ? item.label : type || "-";
    },
    getTypeColor(type) {
      const item = groupTypeDic.find((i) => i.value === type);
      return item ? item.color : "#909399";
    },
    getEnabledPriceCount(row) {
      return allPriceProps.filter(
        (k) => row[k] !== null && row[k] !== undefined && row[k] !== ""
      ).length;
    },
    /* ====== 类型变化时清理无关字段 ====== */
    handleTypeChange(newType) {
      const validProps = (priceSectionsMap[newType] || [])
        .flatMap((s) => s.fields.map((f) => f.prop));
      const validSet = new Set(validProps);
      // 重置不在当前类型下的字段
      Object.keys(this.enabledMap).forEach((prop) => {
        if (!validSet.has(prop) && this.enabledMap[prop]) {
          this.$set(this.enabledMap, prop, false);
          this.$set(this.formData, prop, "");
        }
      });
    },
    /* ====== 表单 ====== */
    resetForm() {
      this.formData = buildEmptyForm();
      this.enabledMap = {};
      this.isEdit = false;
      this.extraItems = [];
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate();
      });
    },
    handleAdd() {
      this.resetForm();
      this.fetchRootDepartId();
      this.dialogVisible = true;
    },
    handleEdit(row) {
      this.resetForm();
      this.isEdit = true;
      // 回显基础字段
      this.formData.id = row.id;
      this.formData.groupName = row.groupName || "";
      this.formData.sort = Number(row.sort) || 0;
      this.formData.type = row.type || "";
      this.formData.rootDepartId = row.rootDepartId || "";
      // 优先使用行数据的 rootDepartId，未返回时则拉取当前部门
      if (!this.rootDepartId && row.rootDepartId) {
        this.rootDepartId = row.rootDepartId;
      } else if (!this.rootDepartId) {
        this.fetchRootDepartId();
      }
      // 回显价格字段：值非空即视为已启用
      allPriceProps.forEach((prop) => {
        const v = row[prop];
        if (v !== null && v !== undefined && v !== "") {
          this.$set(this.formData, prop, v);
          this.$set(this.enabledMap, prop, true);
        }
      });
      this.dialogVisible = true;
      // 加载增项配置
      this.loadExtraItems();
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return;
        // 构建提交数据：仅基础字段 + 已启用的价格字段
        const data = {
          groupName: this.formData.groupName,
          sort: this.formData.sort,
          type: this.formData.type,
          rootDepartId: this.rootDepartId || this.formData.rootDepartId || "",
        };
        if (this.isEdit) data.id = this.formData.id;
        const validProps = (priceSectionsMap[this.formData.type] || [])
          .flatMap((s) => s.fields.map((f) => f.prop));
        validProps.forEach((prop) => {
          if (this.isEnabled(prop)) {
            const v = this.formData[prop];
            data[prop] = v === "" || v === null ? null : Number(v);
          } else if (this.isEdit) {
            // 编辑模式：用户主动移除的字段显式置空
            data[prop] = null;
          }
        });
        this.submitLoading = true;
        const req = this.isEdit ? editAcct(data) : addTask(data);
        req
          .then((response) => {
            if (response.code !== "200") {
              this.$message.error(response.message || (this.isEdit ? "编辑失败" : "新增失败"));
              return;
            }
            // 新增模式下，如有本地缓存增项，需要拿到新分组 ID 后批量创建
            const pendingItems = this.isEdit ? [] : (this.extraItems || []).filter((i) => !i.id);
            const afterFinish = (msg) => {
              this.$message.success(msg || (this.isEdit ? "分组编辑成功" : "分组新增成功"));
              this.dialogVisible = false;
              this.handleList();
            };
            if (pendingItems.length === 0) {
              afterFinish();
              return;
            }
            // 尝试从响应中获取新分组 ID
            this.resolveNewGroupId(response, data)
              .then((newGroupId) => {
                if (!newGroupId) {
                  this.$message.warning("分组创建成功，但未获取到 ID，请手动进入该分组补充增项配置");
                  afterFinish(" ");
                  return;
                }
                return this.batchCreateExtraItems(newGroupId, pendingItems).then((failedCount) => {
                  if (failedCount > 0) {
                    this.$message.warning(`分组已创建，${pendingItems.length - failedCount}/${pendingItems.length} 个增项保存成功，剩余项请进入分组重试`);
                  } else {
                    this.$message.success(`分组与 ${pendingItems.length} 个增项均保存成功`);
                  }
                  this.dialogVisible = false;
                  this.handleList();
                });
              })
              .catch((err) => {
                console.log("submit pending extra items error:", err);
                afterFinish("分组已创建，增项保存异常请重试");
              });
          })
          .catch((err) => {
            console.log("submitForm error:", err);
            this.$message.error("操作异常");
          })
          .finally(() => {
            this.submitLoading = false;
          });
      });
    },
    // 从新增响应/列表兑现出新分组 ID
    resolveNewGroupId(response, data) {
      // 先尝试从响应体取
      try {
        if (response && response.data) {
          const raw = typeof response.data === "string" ? JSON.parse(response.data) : response.data;
          if (raw && raw.id) return Promise.resolve(raw.id);
          if (typeof raw === "number" || typeof raw === "string") return Promise.resolve(raw);
        }
      } catch (_) {
        // ignore
      }
      // 兑底：查列表取同名同类型的分组
      return findUser({ groupName: data.groupName, type: data.type, page: 1, size: 20 })
        .then((res) => {
          try {
            const list = JSON.parse(res.data) || {};
            const rows = list.rows || [];
            const matched = rows.find((r) => r.groupName === data.groupName && r.type === data.type);
            return matched ? matched.id : "";
          } catch (e) {
            return "";
          }
        })
        .catch(() => "");
    },
    // 批量创建本地增项，返回失败数
    batchCreateExtraItems(groupId, items) {
      const tasks = items.map((item) =>
        addExtraItem({
          fieldName: item.fieldName,
          aliasName: item.aliasName,
          merchantAliasName: item.merchantAliasName || "",
          groupId,
          calcType: item.calcType,
          enabled: item.enabled === 0 ? 0 : 1,
          includeInMerchant: item.includeInMerchant === 1 ? 1 : 0,
          unitPrice: (item.calcType === "MULTIPLY" || item.calcType === "AREA_TOTAL" || item.calcType === "FIXED") ? item.unitPrice : null,
          threshold: (item.calcType === "AREA_TOTAL" || item.calcType === "MULTIPLY") ? (item.threshold != null && item.threshold !== '' ? Number(item.threshold) : null) : null,
          mutexGroup: item.mutexGroup || null,
          mutexPriority: item.mutexGroup ? (item.mutexPriority != null ? Number(item.mutexPriority) : 0) : 0,
          minAmount: item.minAmount != null && item.minAmount !== '' ? Number(item.minAmount) : null,
          merchantAreaFlag: item.calcType === "BUILTIN" ? undefined : (item.merchantAreaFlag === 1 ? 1 : 0),
        })
          .then((res) => (res && res.code === "200" ? 0 : 1))
          .catch(() => 1)
      );
      return Promise.all(tasks).then((arr) => arr.reduce((s, n) => s + n, 0));
    },
    /* ====== 增项配置 ====== */
    loadExtraItems() {
      if (!this.formData.id) {
        this.extraItems = [];
        return;
      }
      this.extraLoading = true;
      findExtraItem(this.formData.id)
        .then((res) => {
          if (res.code === "200") {
            const data = res.data
              ? typeof res.data === "string"
                ? JSON.parse(res.data)
                : res.data
              : [];
            this.extraItems = Array.isArray(data) ? data : data.rows || [];
          } else {
            this.extraItems = [];
            this.$message.error("获取增项列表失败");
          }
        })
        .catch((err) => {
          console.log("loadExtraItems error:", err);
          this.extraItems = [];
        })
        .finally(() => {
          this.extraLoading = false;
        });
    },
    openExtraDialog(row) {
      this.extraForm = buildEmptyExtraForm();
      this.extraForm.groupId = this.formData.id;
      if (row && (row.id || row._localId)) {
        this.extraForm.id = row.id || "";
        this.extraForm._localId = row._localId || "";
        this.extraForm.fieldName = row.fieldName || "";
        this.extraForm.aliasName = row.aliasName || "";
        this.extraForm.merchantAliasName = row.merchantAliasName || "";
        this.extraForm.calcType = row.calcType || "DIRECT";
        this.extraForm.unitPrice = row.unitPrice != null ? Number(row.unitPrice) : 0;
        this.extraForm.threshold = row.threshold != null ? Number(row.threshold) : null;
        this.extraForm.enabled = row.enabled === 0 ? 0 : 1;
        this.extraForm.includeInMerchant = row.includeInMerchant === 1 ? 1 : 0;
        this.extraForm.merchantAreaFlag = row.merchantAreaFlag === 1 ? 1 : 0;
        this.extraForm.groupId = row.groupId || this.formData.id;
        this.extraForm.mutexGroup = row.mutexGroup || "";
        this.extraForm.mutexPriority = row.mutexPriority != null ? Number(row.mutexPriority) : 0;
        this.extraForm.minAmount = row.minAmount != null ? Number(row.minAmount) : null;
      }
      this.extraDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.extraForm) this.$refs.extraForm.clearValidate();
      });
    },
    // 商家面积计价开关联动：开启时强制关闭「计入商家」
    onMerchantAreaFlagChange(val) {
      this.extraForm.merchantAreaFlag = val ? 1 : 0;
      if (val) {
        this.extraForm.includeInMerchant = 0;
      }
    },
    submitExtra() {
      this.$refs.extraForm.validate((valid) => {
        if (!valid) return;
        const isLocal = !this.formData.id; // 新增分组模式
        const payload = {
          fieldName: this.extraForm.fieldName,
          aliasName: this.extraForm.aliasName,
          merchantAliasName: this.extraForm.merchantAliasName || "",
          groupId: this.extraForm.groupId || this.formData.id || "",
          calcType: this.extraForm.calcType,
          enabled: this.extraForm.enabled === 0 ? 0 : 1,
          includeInMerchant: this.extraForm.includeInMerchant === 1 ? 1 : 0,
          unitPrice:
            (this.extraForm.calcType === "MULTIPLY" || this.extraForm.calcType === "AREA_TOTAL" || this.extraForm.calcType === "FIXED") ? this.extraForm.unitPrice : null,
          threshold: (this.extraForm.calcType === "AREA_TOTAL" || this.extraForm.calcType === "MULTIPLY") ? (this.extraForm.threshold != null && this.extraForm.threshold !== '' ? Number(this.extraForm.threshold) : null) : null,
          mutexGroup: this.extraForm.mutexGroup || null,
          mutexPriority: this.extraForm.mutexGroup ? (this.extraForm.mutexPriority != null ? Number(this.extraForm.mutexPriority) : 0) : 0,
          minAmount: this.extraForm.minAmount != null && this.extraForm.minAmount !== '' ? Number(this.extraForm.minAmount) : null,
          merchantAreaFlag: this.extraForm.calcType === "BUILTIN" ? undefined : (this.extraForm.merchantAreaFlag === 1 ? 1 : 0),
        };
        // 新增分组模式：本地缓存增项
        if (isLocal) {
          if (this.extraForm._localId) {
            const idx = this.extraItems.findIndex((i) => i._localId === this.extraForm._localId);
            if (idx > -1) this.$set(this.extraItems, idx, { ...payload, _localId: this.extraForm._localId });
          } else {
            this.extraItems.push({ ...payload, _localId: `local_${Date.now()}_${Math.random()}` });
          }
          this.$message.success(this.extraForm._localId ? "增项已更新（将随分组一并保存）" : "增项已添加（将随分组一并保存）");
          this.extraDialogVisible = false;
          return;
        }
        // 编辑分组模式：调用后台接口
        if (this.extraForm.id) payload.id = this.extraForm.id;
        this.extraSubmitLoading = true;
        const req = this.extraForm.id ? updateExtraItem(payload) : addExtraItem(payload);
        req
          .then((res) => {
            if (res.code === "200") {
              this.$message.success(this.extraForm.id ? "增项编辑成功" : "增项添加成功");
              this.extraDialogVisible = false;
              this.loadExtraItems();
            } else {
              this.$message.error(res.message || "操作失败");
            }
          })
          .catch((err) => {
            console.log("submitExtra error:", err);
            this.$message.error("操作异常");
          })
          .finally(() => {
            this.extraSubmitLoading = false;
          });
      });
    },
    deleteExtra(row) {
      if (!row) return;
      const isLocal = !this.formData.id || !row.id;
      this.$confirm(`确定删除增项「${row.fieldName}」吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          if (isLocal) {
            // 本地缓存：直接从数组移除
            this.extraItems = this.extraItems.filter((i) =>
              row._localId ? i._localId !== row._localId : i !== row
            );
            this.$message.success("已移除");
            return;
          }
          deleteExtraItem(row.id)
            .then((res) => {
              if (res.code === "200") {
                this.$message.success("删除成功");
                this.loadExtraItems();
              } else {
                this.$message.error(res.message || "删除失败");
              }
            })
            .catch((err) => {
              console.log("deleteExtra error:", err);
              this.$message.error("删除异常");
            });
        })
        .catch(() => {});
    },
    toggleExtraEnabled(row, val) {
      const target = val ? 1 : 0;
      // 本地缓存（新增分组模式）直接修改
      if (!row.id) {
        this.$set(row, "enabled", target);
        return;
      }
      const payload = {
        id: row.id,
        fieldName: row.fieldName,
        aliasName: row.aliasName,
        merchantAliasName: row.merchantAliasName || "",
        groupId: row.groupId || this.formData.id,
        calcType: row.calcType,
        includeInMerchant: row.includeInMerchant === 1 ? 1 : 0,
        unitPrice: (row.calcType === "MULTIPLY" || row.calcType === "AREA_TOTAL" || row.calcType === "FIXED") ? row.unitPrice : null,
        threshold: (row.calcType === "AREA_TOTAL" || row.calcType === "MULTIPLY") ? (row.threshold != null && row.threshold !== '' ? Number(row.threshold) : null) : null,
        mutexGroup: row.mutexGroup || null,
        mutexPriority: row.mutexGroup ? (row.mutexPriority != null ? Number(row.mutexPriority) : 0) : 0,
        minAmount: row.minAmount != null && row.minAmount !== '' ? Number(row.minAmount) : null,
        merchantAreaFlag: row.calcType === "BUILTIN" ? undefined : (row.merchantAreaFlag === 1 ? 1 : 0),
        enabled: target,
      };
      updateExtraItem(payload)
        .then((res) => {
          if (res.code === "200") {
            this.$set(row, "enabled", target);
            this.$message.success(target === 1 ? "已启用" : "已禁用");
          } else {
            this.$message.error(res.message || "状态切换失败");
          }
        })
        .catch((err) => {
          console.log("toggleExtraEnabled error:", err);
          this.$message.error("状态切换异常");
        });
    },
    toggleIncludeInMerchant(row, val) {
      const target = val ? 1 : 0;
      // 本地缓存（新增分组模式）直接修改
      if (!row.id) {
        this.$set(row, "includeInMerchant", target);
        return;
      }
      const payload = {
        id: row.id,
        fieldName: row.fieldName,
        aliasName: row.aliasName,
        merchantAliasName: row.merchantAliasName || "",
        groupId: row.groupId || this.formData.id,
        calcType: row.calcType,
        unitPrice: (row.calcType === "MULTIPLY" || row.calcType === "AREA_TOTAL" || row.calcType === "FIXED") ? row.unitPrice : null,
        threshold: (row.calcType === "AREA_TOTAL" || row.calcType === "MULTIPLY") ? (row.threshold != null && row.threshold !== '' ? Number(row.threshold) : null) : null,
        mutexGroup: row.mutexGroup || null,
        mutexPriority: row.mutexGroup ? (row.mutexPriority != null ? Number(row.mutexPriority) : 0) : 0,
        minAmount: row.minAmount != null && row.minAmount !== '' ? Number(row.minAmount) : null,
        merchantAreaFlag: row.calcType === "BUILTIN" ? undefined : (row.merchantAreaFlag === 1 ? 1 : 0),
        enabled: row.enabled === 1 ? 1 : 0,
        includeInMerchant: target,
      };
      updateExtraItem(payload)
        .then((res) => {
          if (res.code === "200") {
            this.$set(row, "includeInMerchant", target);
            this.$message.success(target === 1 ? "已计入商家费用" : "已不计入商家费用");
          } else {
            this.$message.error(res.message || "状态切换失败");
          }
        })
        .catch((err) => {
          console.log("toggleIncludeInMerchant error:", err);
          this.$message.error("状态切换异常");
        });
    },
    // 互斥组颜色池
    getMutexGroupColor(group) {
      if (!group) return "#909399";
      const colors = ["#409eff", "#e6a23c", "#67c23a", "#f56c6c", "#909399", "#0bb89b", "#9b59b6", "#e74c3c"];
      let hash = 0;
      for (let i = 0; i < group.length; i++) {
        hash = group.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },
    /* ====== 列表 ====== */
    handleView(row) {
      this.currentGroupId = row.id;
      this.detailVisible = true;
    },
    closeDetail() {
      this.detailVisible = false;
      this.currentGroupId = "";
    },
    handleSelectionChange() {},
    handleDelete() {
      const selections = this.$refs.crud.$refs.table.selection;
      if (!selections || selections.length === 0) {
        this.$message.warning("请先选择要删除的分组");
        return;
      }
      this.$confirm(`确定要删除选中的 ${selections.length} 个分组吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          const ids = selections.map((item) => item.id).join(",");
          this.tableLoading = true;
          deleteAccountGroup(ids)
            .then((response) => {
              if (response.code === "200") {
                this.$message.success("删除成功");
                this.handleList();
              } else {
                this.$message.error("删除失败：" + (response.message || "未知错误"));
              }
            })
            .catch((error) => {
              console.log("删除分组异常:", error);
              this.$message.error("删除异常");
            })
            .finally(() => {
              this.tableLoading = false;
            });
        })
        .catch(() => {});
    },
    buildQuery(extra = {}) {
      return {
        page: this.page.currentPage,
        size: this.page.pageSize,
        ...this.searchParams,
        ...extra,
      };
    },
    handleList(extra = {}) {
      this.tableLoading = true;
      findUser(this.buildQuery(extra))
        .then((res) => {
          const data = JSON.parse(res.data) || {};
          this.tableData = data.rows || [];
          this.page.total = data.total || 0;
        })
        .catch((err) => {
          console.log("handleList error:", err);
          this.tableData = [];
          this.page.total = 0;
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    pageChange(page) {
      this.page.currentPage = page;
      this.handleList();
    },
    pageSizeChange(pageSize) {
      this.page.pageSize = pageSize;
      this.handleList();
    },
    searchChange(params, done) {
      this.page.currentPage = 1;
      this.searchParams = {};
      if (params.groupName) this.searchParams.groupName = params.groupName;
      if (params.type) this.searchParams.type = params.type;
      this.handleList();
      if (typeof done === "function") done();
    },
    refreshChange(page) {
      this.page.currentPage = page.currentPage;
      this.page.pageSize = page.pageSize;
      this.handleList();
    },
  },
};
</script>

<style lang="scss" scoped>
.ag-dialog {
  ::v-deep .el-dialog__body {
    padding: 16px 24px 8px;
    max-height: 65vh;
    overflow-y: auto;
  }
}

.ag-section {
  margin-bottom: 16px;
  padding: 12px 16px 4px;
  background: #fafbfc;
  border: 1px solid #ebeef5;
  border-radius: 6px;

  &--base {
    background: linear-gradient(135deg, #f5f9ff 0%, #fafbfc 100%);
  }

  &--extra {
    background: linear-gradient(135deg, #fff7e6 0%, #fafbfc 100%);
    border-color: #faecd8;

    .ag-section__head i {
      color: #e6a23c;
    }
  }

  &__head {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    color: #303133;
    font-size: 14px;
    font-weight: 600;

    i {
      margin-right: 6px;
      color: #409eff;
      font-size: 16px;
    }
  }

  &__title {
    margin-right: 10px;
  }

  &__desc {
    margin-right: auto;
    color: #909399;
    font-size: 12px;
    font-weight: normal;
  }
}

.ag-fields {
  ::v-deep .el-form-item {
    margin-bottom: 14px;
  }
}

.ag-suffix {
  color: #909399;
  font-size: 12px;
  line-height: 32px;
  padding-right: 4px;
}

.ag-chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 0 8px;

  &__hint {
    color: #909399;
    font-size: 12px;
    margin-right: 4px;
  }
}

.ag-chip {
  cursor: pointer;
  border-style: dashed;
  transition: all 0.2s;

  &:hover {
    color: #409eff;
    border-color: #409eff;
    background: #ecf5ff;
  }
}

.ag-empty {
  padding: 24px;
  text-align: center;
  color: #909399;
  font-size: 13px;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  margin-bottom: 12px;

  i {
    margin-right: 6px;
  }
}

/* 计算方式卡片选择器 */
.calc-type-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  width: 100%;
}

.calc-type-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    border-color: #c0c4cc;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }

  &.is-active {
    border-color: #409eff;
    background: linear-gradient(135deg, #ecf5ff 0%, #fff 70%);
    box-shadow: 0 2px 10px rgba(64, 158, 255, 0.18);
  }

  &__icon {
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  &__body {
    flex: 1;
    min-width: 0;
    line-height: 1.4;
  }

  &__title {
    font-size: 13px;
    color: #303133;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__desc {
    font-size: 12px;
    color: #909399;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__check {
    position: absolute;
    top: 6px;
    right: 6px;
    color: #409eff;
    font-size: 16px;
  }
}

.form-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;

  i {
    color: #e6a23c;
    margin-right: 4px;
  }
}

/* 互斥配置区块 */
.mutex-section {
  margin-top: 8px;
  padding: 12px 16px 4px;
  background: linear-gradient(135deg, #f0f5ff 0%, #fafbfc 100%);
  border: 1px solid #d6e4ff;
  border-radius: 6px;

  &__head {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    color: #303133;
    font-size: 14px;
    font-weight: 600;

    i {
      margin-right: 6px;
      color: #409eff;
      font-size: 16px;
    }
  }

  &__title {
    margin-right: 10px;
  }

  &__desc {
    color: #909399;
    font-size: 12px;
    font-weight: normal;
  }

  &__tip {
    margin-bottom: 12px;
    padding: 8px 12px;
    font-size: 12px;
    color: #606266;
    background: #fffbe6;
    border: 1px solid #ffe58f;
    border-radius: 4px;
    line-height: 1.6;

    i {
      color: #e6a23c;
      margin-right: 4px;
    }
  }

  ::v-deep .el-form-item {
    margin-bottom: 14px;
  }
}
</style>
